import { Customer } from '../types';
import { DuplicateHit, findSimilarCustomers, nameSimilarity, normalizeCompanyName } from './customerDuplicates';
import { composePersonName, normalizeSalutation, parsePersonName } from './personName';
import {
    cleanExtfValue,
    EXTF_COL,
    extfColumnIndex,
    looksLikeCompanyName,
} from './datevExtf';

/**
 * Kundenlisten-Import (Steuerberater-Liste u. ae.): CSV/Excel-Zeilen oder
 * PDF-Extraktion in Abgleichszeilen gegen den Bestand.
 *
 * Pur — Dateizugriff, Excel-Parser und Gemini liegen bei den Aufrufern.
 * Kern-Regel: die KUNDENNUMMER ist der Schluessel — ein Nummern-Treffer heisst
 * "vorhanden". AUSSER der Name passt ueberhaupt nicht zum Bestandskunden: dann
 * hat die Liste einen anderen Nummernkreis und die Zeile ist ein
 * Nummernkonflikt, kein stiller Treffer (Status number_conflict).
 */

/** Eine Zeile der eingelesenen Liste, bereits auf Zielfelder gemappt. */
export interface ImportedCustomerRow {
    customerNumber?: string;
    name: string;
    street?: string;
    zip?: string;
    city?: string;
    /** Zusammengesetzte Anzeige-Adresse; von rowsToImportRows befuellt. */
    address?: string;
    email?: string;
    phone?: string;
    /**
     * Unternehmen oder Privatperson. Bleibt undefined, wenn die Quelle nichts
     * dazu hergibt — die Anlage laesst das Feld dann leer, was als Unternehmen
     * gelesen wird (Customer.customerType).
     */
    customerType?: 'company' | 'person';
    salutation?: string;
    title?: string;
    /** Nur bei Privatpersonen; `name` bleibt der komponierte Vollname. */
    firstName?: string;
    lastName?: string;
}

export type ImportColumnKey =
    | 'customerNumber'
    | 'name'
    | 'firstName'
    | 'lastName'
    | 'street'
    | 'zip'
    | 'city'
    | 'email'
    | 'phone'
    | 'salutation'
    | 'title';

export type ColumnMapping = Partial<Record<ImportColumnKey, string>>;

/**
 * Bytes einer hochgeladenen CSV/TXT in Text. Deutsche Excel- und
 * DATEV-Exporte sind windows-1252/ISO-8859-1, der eigene Export
 * (customerListExport.ts) ist UTF-8 mit BOM — falsch geraten, zerstoert es
 * genau die Umlaute in Namen und Orten.
 * Regel: UTF-8, wenn die Bytes gueltiges UTF-8 OHNE Ersatzzeichen sind,
 * sonst windows-1252.
 */
export const decodeListText = (bytes: ArrayBuffer | Uint8Array): string => {
    const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    // windows-1252 ist die Kodierung der DATEV-/Excel-Exporte; der Alias
    // 'iso-8859-1' zeigt im Browser auf dieselbe Tabelle.
    const fallback = () => new TextDecoder('windows-1252').decode(view);
    let text: string;
    try {
        text = new TextDecoder('utf-8', { fatal: true }).decode(view);
    } catch {
        return fallback();
    }
    // Zweites Netz: manche Dateien sind gueltiges UTF-8 UND enthalten trotzdem
    // Ersatzzeichen (U+FFFD) — dann war die Quelle schon einmal falsch
    // dekodiert worden. Aus den Rohbytes gelesen stimmen die Umlaute wieder.
    return text.includes('�') ? fallback() : text;
};

/** Kopfzeile ohne die Inhalte in Anfuehrungszeichen — nur echte Trenner. */
const unquotedHeadLine = (text: string): string => {
    let out = '';
    let quoted = false;
    for (const ch of text) {
        if (ch === '"') {
            quoted = !quoted;
            continue;
        }
        if (!quoted && ch === '\n') break;
        if (!quoted) out += ch;
    }
    return out;
};

/**
 * Quote-festes Aufteilen: "" innerhalb von Anfuehrungszeichen ist ein echtes
 * Anfuehrungszeichen, Trenner und Zeilenumbrueche darin gehoeren zum Wert.
 */
const splitTable = (text: string, delimiter: string): string[][] => {
    const table: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let quoted = false;
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        if (quoted) {
            if (ch !== '"') {
                cell += ch;
            } else if (text[i + 1] === '"') {
                cell += '"';
                i += 1;
            } else {
                quoted = false;
            }
            continue;
        }
        if (ch === '"') quoted = true;
        else if (ch === delimiter) {
            row.push(cell);
            cell = '';
        } else if (ch === '\n') {
            row.push(cell);
            table.push(row);
            row = [];
            cell = '';
        } else cell += ch;
    }
    row.push(cell);
    table.push(row);
    return table.map((r) => r.map((c) => c.trim()));
};

/**
 * Trennzeichen-Text (CSV/TSV) in Kopfzeile + Zeilen. Trenner-Erkennung ueber
 * die Kopfzeile: Semikolon (deutsches Excel) vor Tab vor Komma.
 *
 * Quote-fest, damit der eigene Export (customerListExport.ts) wieder
 * einlesbar ist: dort werden Werte mit Semikolon, Anfuehrungszeichen oder
 * Zeilenumbruch in Anfuehrungszeichen gesetzt.
 */
export const parseDelimitedText = (text: string): { headers: string[]; rows: string[][] } => {
    const src = text.replace(/^\ufeff/, '').replace(/\r\n/g, '\n');
    const head = unquotedHeadLine(src);
    const delimiter = head.includes(';') ? ';' : head.includes('\t') ? '\t' : ',';
    const table = splitTable(src, delimiter).filter((r) => r.some((c) => c !== ''));
    if (!table.length) return { headers: [], rows: [] };
    return { headers: table[0], rows: table.slice(1) };
};

/**
 * Erkennungsmuster je Zielfeld — Steuerberater-/DATEV-uebliche Bezeichnungen.
 * Je Feld GEORDNET von genau nach unscharf: die Kopfzeile des eigenen Exports
 * enthaelt sowohl "Kontenbezeichnung" (auf 40 Zeichen gekuerzt) als auch
 * "Name" (vollstaendig) — ohne Reihenfolge gewaenne die gekuerzte Spalte und
 * jeder Export/Import-Durchlauf wuerde den Namen still abschneiden.
 */
const COLUMN_PATTERNS: Record<ImportColumnKey, RegExp[]> = {
    customerNumber: [/kunden[\s.\-]*nr|kundennummer/i, /kto[\s.\-]*nr/i, /debitor|konto/i],
    name: [/^name$/i, /firma|firmenname|bezeichnung|kunde$/i],
    // Vorname VOR Name pruefen waere falsch herum: die Muster laufen je Feld,
    // und "^vorname$" trifft niemals das Namensmuster.
    firstName: [/^vorname$/i, /vorname/i],
    lastName: [/^nachname$/i, /nachname|familienname|zuname/i],
    street: [/stra(ss|ß)e|^str\.?$/i],
    zip: [/^plz$|postleitzahl/i],
    city: [/^ort$|stadt/i],
    email: [/mail/i],
    phone: [/telefon|^tel\.?$|phone/i],
    salutation: [/^anrede$/i, /anrede|salutation/i],
    title: [/^titel$/i, /^title$/i],
};

/**
 * Automatische Spaltenzuordnung — der Nutzer korrigiert im Modal nach.
 * Das fruehere Muster gewinnt: erst wenn KEINE Spalte darauf passt, wird das
 * naechste, unschaerfere Muster geprueft.
 */
export const autoMapColumns = (headers: string[]): ColumnMapping => {
    const mapping: ColumnMapping = {};
    for (const key of Object.keys(COLUMN_PATTERNS) as ImportColumnKey[]) {
        for (const pattern of COLUMN_PATTERNS[key]) {
            const hit = headers.find((h) => pattern.test(h.trim()));
            if (hit) {
                mapping[key] = hit;
                break;
            }
        }
    }
    return mapping;
};

/** Zeilen nach Zuordnung in Zielform; Zeilen ohne Namen entfallen. */
export const rowsToImportRows = (
    headers: string[],
    rows: string[][],
    mapping: ColumnMapping,
): ImportedCustomerRow[] => {
    const idx = new Map(headers.map((h, i) => [h, i]));
    const cell = (row: string[], key: ImportColumnKey): string | undefined => {
        const header = mapping[key];
        if (!header) return undefined;
        const i = idx.get(header);
        const v = i !== undefined ? row[i]?.trim() : undefined;
        return v || undefined;
    };
    const out: ImportedCustomerRow[] = [];
    for (const row of rows) {
        const nameCell = cell(row, 'name');
        const firstCell = cell(row, 'firstName');
        const lastCell = cell(row, 'lastName');
        // Eine gefuellte Anrede ist das einzige verlaessliche Personen-Signal
        // einer Tabelle ohne Namensspalten-Trennung. Ohne sie bleibt der Typ
        // offen (= Unternehmen) — aus dem Namen zu raten wuerde Einzelfirmen
        // falsch einordnen.
        const salutationCell = cell(row, 'salutation');
        const titleCell = cell(row, 'title');
        const person = splitPersonColumns(firstCell, lastCell, salutationCell, titleCell);
        // Getrennte Namensspalten gewinnen ueber die Namensspalte: nur aus
        // ihnen steht fest, wo der Vorname aufhoert. `name` bleibt trotzdem
        // der Vollname — er wird hier komponiert.
        const name = person ? composePersonName(person.firstName, person.lastName) || nameCell : nameCell;
        if (!name) continue;
        const street = cell(row, 'street');
        const zip = cell(row, 'zip');
        const city = cell(row, 'city');
        const place = [zip, city].filter(Boolean).join(' ');
        const salutation = person?.salutation ?? salutationCell;
        out.push({
            customerNumber: cell(row, 'customerNumber'),
            name,
            firstName: person?.firstName,
            lastName: person?.lastName,
            street,
            zip,
            city,
            address: [street, place].filter(Boolean).join(', ') || undefined,
            email: cell(row, 'email'),
            phone: cell(row, 'phone'),
            salutation,
            title: person?.title ?? titleCell,
            customerType: person || salutation ? 'person' : undefined,
        });
    }
    return out;
};

/**
 * Getrennte Vorname-/Nachname-Spalten aufbereiten. Anrede und Titel stecken in
 * solchen Listen haeufig MIT im Namensfeld ("Frau Hocke" / "Dr. Akbar") — sie
 * werden herausgezogen, aber nur dort eingesetzt, wo die Tabelle keine eigene
 * Spalte dafuer gefuellt hat.
 *
 * Gibt undefined zurueck, wenn die Tabelle gar keine Namenstrennung hat.
 */
const splitPersonColumns = (
    firstCell: string | undefined,
    lastCell: string | undefined,
    salutationCell: string | undefined,
    titleCell: string | undefined,
): { firstName?: string; lastName?: string; salutation?: string; title?: string } | undefined => {
    if (!firstCell && !lastCell) return undefined;
    const parsed = parsePersonName(
        firstCell && lastCell ? `${lastCell}, ${firstCell}` : (lastCell ?? firstCell ?? ''),
    );
    const salutation = salutationCell ?? parsed.salutation;
    const title = titleCell ?? parsed.title;
    if (firstCell && lastCell) {
        return { firstName: parsed.firstName ?? firstCell, lastName: parsed.lastName ?? lastCell, salutation, title };
    }
    if (lastCell) {
        return { firstName: parsed.firstName, lastName: parsed.lastName ?? lastCell, salutation, title };
    }
    // Nur eine Vornamen-Spalte: der Rest nach Anrede/Titel bleibt Vorname.
    return {
        firstName: composePersonName(parsed.firstName, parsed.lastName) || firstCell,
        lastName: undefined,
        salutation,
        title,
    };
};

/**
 * Gemini-Ergebnis (extractCustomerList, PDF-Weg) in Importzeilen — dieselbe
 * Form wie der CSV/Excel-Weg, damit der Abgleich EINE Implementierung bleibt.
 */
export const importRowsFromAi = (parsed: unknown): ImportedCustomerRow[] => {
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
    const kunden = (parsed as { kunden?: unknown }).kunden;
    if (!Array.isArray(kunden)) return [];
    const t = (v: unknown): string | undefined => {
        const s = typeof v === 'string' ? v.trim() : '';
        return s || undefined;
    };
    const out: ImportedCustomerRow[] = [];
    for (const raw of kunden) {
        if (!raw || typeof raw !== 'object') continue;
        const r = raw as Record<string, unknown>;
        const name = t(r.name);
        if (!name) continue;
        const street = t(r.strasse);
        const zip = t(r.plz);
        const city = t(r.ort);
        const place = [zip, city].filter(Boolean).join(' ');
        // Typ nur uebernehmen, wenn das Modell sich festgelegt hat. Fehlt das
        // Feld (aeltere Antwort, Modell laesst es weg), bleibt der Typ offen —
        // die Anlage liest das als Unternehmen, wie beim Altbestand.
        const typ = t(r.typ)?.toLowerCase();
        const customerType = typ ? (typ === 'privatperson' ? 'person' : 'company') : undefined;
        // Vor-/Nachname liefert das Modell nur bei Privatpersonen. Fehlen sie
        // dort, wird der Name mit derselben Regel zerlegt wie beim
        // Datei-Import — der Vollname bleibt in jedem Fall `name`.
        const person =
            customerType === 'person'
                ? splitPersonColumns(t(r.vorname), t(r.nachname), t(r.anrede), t(r.titel))
                    ?? parsePersonName(name)
                : undefined;
        out.push({
            customerNumber: t(r.nummer),
            name: person ? composePersonName(person.firstName, person.lastName) || name : name,
            firstName: person?.firstName,
            lastName: person?.lastName,
            street,
            zip,
            city,
            address: [street, place].filter(Boolean).join(', ') || undefined,
            email: t(r.email),
            phone: t(r.telefon),
            customerType,
            salutation: person?.salutation ?? t(r.anrede),
            title: person?.title ?? t(r.titel),
        });
    }
    return out;
};

/**
 * DATEV-EXTF-Datei? Zelle A1 traegt bei diesem Format immer "EXTF"; die
 * eigentliche Kopfzeile steht darunter. Der generische Tabellen-Leser haelt
 * darum Zeile 1 (Metadaten) faelschlich fuer den Kopf — genau daran wird das
 * Format erkannt.
 */
export const isDatevExtfTable = (headers: string[]): boolean =>
    (headers[0] ?? '').trim().toUpperCase() === 'EXTF';

/**
 * DATEV-EXTF (Debitoren/Kreditoren) in Importzeilen.
 *
 * Die Spaltenzuordnung steht bei diesem Format fest und laeuft ueber die
 * Spaltennamen der Datei — die manuelle Zuordnung im Modal entfaellt.
 *
 * Die Typ-Erkennung traut dem Feld "Adressattyp" NICHT: im echten
 * Kanzlei-Export steht dort ausnahmslos 1 (natuerliche Person), und der volle
 * Firmenname steht doppelt in "Name (natuerl. Person)" UND "Vorname (natuerl.
 * Person)". Entschieden wird darum in dieser Reihenfolge:
 *   1. Unternehmens-Namensspalte gefuellt  -> Unternehmen
 *   2. Adressattyp exakt "2"               -> Unternehmen (unser eigener Export)
 *   3. Name != Vorname, beide gefuellt     -> Person aus beiden Spalten
 *   4. sonst EIN Rohname: Anrede-Spalte oder Firmen-Heuristik entscheidet
 */
export const extfTableToImportRows = (
    headers: string[],
    rows: string[][],
): ImportedCustomerRow[] => {
    if (!isDatevExtfTable(headers) || rows.length < 2) return [];
    const columns = rows[0];
    const data = rows.slice(1);
    const index = (name: string) => extfColumnIndex(columns, name);
    const cols = {
        account: index(EXTF_COL.account),
        company: index(EXTF_COL.companyName),
        personLast: index(EXTF_COL.personLastName),
        personFirst: index(EXTF_COL.personFirstName),
        unknown: index(EXTF_COL.unknownName),
        addressType: index(EXTF_COL.addressType),
        salutation: index(EXTF_COL.salutation),
        title: index(EXTF_COL.title),
        street: index(EXTF_COL.street),
        zip: index(EXTF_COL.zip),
        city: index(EXTF_COL.city),
        phone: index(EXTF_COL.phone),
        email: index(EXTF_COL.email),
    };
    const at = (row: string[], i: number): string => (i >= 0 ? cleanExtfValue(row[i]) : '');

    const out: ImportedCustomerRow[] = [];
    for (const row of data) {
        const companyName = at(row, cols.company);
        const persLast = at(row, cols.personLast);
        const persFirst = at(row, cols.personFirst);
        const unknownName = at(row, cols.unknown);
        const salutationCol = normalizeSalutation(at(row, cols.salutation));
        const titleCol = at(row, cols.title) || undefined;
        const addressType = at(row, cols.addressType);

        let name = '';
        let customerType: 'company' | 'person' | undefined;
        let firstName: string | undefined;
        let lastName: string | undefined;
        let salutation = salutationCol;
        let title = titleCol;

        const sameName = !!persLast && persLast.toLowerCase() === persFirst.toLowerCase();
        const rawName = companyName || persLast || persFirst || unknownName;
        if (!rawName) continue;

        if (companyName || addressType === '2') {
            name = rawName;
            customerType = 'company';
        } else if (persLast && persFirst && !sameName) {
            const parsed = parsePersonName(`${persLast}, ${persFirst}`);
            firstName = parsed.firstName ?? persFirst;
            lastName = parsed.lastName ?? persLast;
            salutation = salutation ?? parsed.salutation;
            title = title ?? parsed.title;
            name = composePersonName(firstName, lastName) || rawName;
            customerType = 'person';
        } else {
            const parsed = parsePersonName(rawName);
            const remaining = composePersonName(parsed.firstName, parsed.lastName)
                .split(' ')
                .filter(Boolean).length;
            // Eine gefuellte Anrede-Spalte schlaegt die Firmen-Heuristik: sie
            // ist die einzige Stelle, an der die Kanzlei "Privatperson" wirklich
            // erklaert hat.
            if (!salutation && looksLikeCompanyName(rawName, remaining)) {
                name = rawName;
                customerType = 'company';
            } else {
                firstName = parsed.firstName;
                lastName = parsed.lastName;
                salutation = salutation ?? parsed.salutation;
                title = title ?? parsed.title;
                name = composePersonName(firstName, lastName) || rawName;
                customerType = 'person';
            }
        }

        const street = at(row, cols.street) || undefined;
        const zip = at(row, cols.zip) || undefined;
        const city = at(row, cols.city) || undefined;
        const place = [zip, city].filter(Boolean).join(' ');
        out.push({
            customerNumber: at(row, cols.account) || undefined,
            name,
            firstName,
            lastName,
            street,
            zip,
            city,
            address: [street, place].filter(Boolean).join(', ') || undefined,
            email: at(row, cols.email) || undefined,
            phone: at(row, cols.phone) || undefined,
            customerType,
            salutation,
            title,
        });
    }
    return out;
};

/**
 * exists            — Kundennummer matcht einen Bestandskunden, Name passt.
 * number_conflict   — Nummer matcht, aber der Name passt NICHT zum Bestand.
 * similar           — kein Nummern-Treffer, aber Dubletten-Verdacht.
 * new               — unbekannt, Kandidat fuer die Anlage.
 * duplicate_in_file — Nummer, Name oder E-Mail kam in der Liste schon vor.
 */
export type ReconcileStatus =
    | 'exists'
    | 'number_conflict'
    | 'similar'
    | 'new'
    | 'duplicate_in_file';

/** Woran die datei-interne Dublette haengt. */
export type DuplicateReason = 'number' | 'name' | 'email';

export interface ReconciledCustomerRow {
    row: ImportedCustomerRow;
    status: ReconcileStatus;
    /** Bestandskunde bei exists und number_conflict (Halter der Nummer). */
    existing?: Customer;
    hits?: DuplicateHit[];
    /** Index der Zeile, die den Wert zuerst gebracht hat (duplicate_in_file). */
    duplicateOfIndex?: number;
    duplicateReason?: DuplicateReason;
}

/**
 * Kanonische Form einer Kundennummer fuer Vergleiche.
 *
 * Die Datenbank vergibt und prueft Nummern NUMERISCH: der Nummernkreis-RPC
 * vergleicht `customer_number::numeric` gegen die Kandidaten-Nummer
 * (supabase/customer_number_ranges.sql:35-36). Ein rein textlicher Vergleich
 * hier wuerde '030000' und '30000' als verschieden ansehen — obwohl beide
 * denselben Platz im Nummernkreis belegen und die naechste freie Nummer
 * dahinter vergeben wird. Darum: fuehrende Nullen bei reinen Ziffernfolgen
 * wegwerfen, alles andere bleibt textlich (Nummern wie 'D-100').
 */
export const normNumber = (n?: string): string => {
    const t = (n ?? '').trim().toLowerCase();
    return /^\d+$/.test(t) ? String(Number.parseInt(t, 10)) : t;
};

/**
 * Ab dieser Namensaehnlichkeit gilt ein Nummern-Treffer als derselbe Kunde.
 * Darunter ist die Nummer in der Liste an eine ANDERE Firma vergeben — der
 * haeufigste Fall bei Listen aus einem fremden Nummernkreis. Frueher wurde
 * so eine Zeile gruen als "vorhanden" gemeldet und stillschweigend verworfen.
 */
const NUMBER_MATCH_MIN_NAME_SIMILARITY = 0.5;

export const reconcileCustomerList = (
    rows: ImportedCustomerRow[],
    existing: Customer[],
): ReconciledCustomerRow[] => {
    const byNumber = new Map<string, Customer>();
    for (const c of existing) {
        const key = normNumber(c.customerNumber);
        if (key) byNumber.set(key, c);
    }
    // Datei-intern gesehene Schluessel -> Index des Erstauftretens. Nur die
    // ERSTE Zeile eines Werts wird verarbeitet, jede weitere ist eine Dublette:
    // ohne Namens- und E-Mail-Spur legte eine Liste mit zweimal derselben Firma
    // (ohne Nummer) zwei Kunden an.
    const seenNumbers = new Map<string, number>();
    const seenNames = new Map<string, number>();
    const seenEmails = new Map<string, number>();
    return rows.map((row, index) => {
        const key = normNumber(row.customerNumber);
        const nameKey = normalizeCompanyName(row.name || '');
        const mailKey = (row.email ?? '').trim().toLowerCase();

        const numberDup = key ? seenNumbers.get(key) : undefined;
        const nameDup = nameKey ? seenNames.get(nameKey) : undefined;
        const mailDup = mailKey ? seenEmails.get(mailKey) : undefined;
        const dup: { index: number; reason: DuplicateReason } | undefined =
            numberDup !== undefined ? { index: numberDup, reason: 'number' }
            : nameDup !== undefined ? { index: nameDup, reason: 'name' }
            : mailDup !== undefined ? { index: mailDup, reason: 'email' }
            : undefined;
        if (dup) {
            return {
                row,
                status: 'duplicate_in_file' as const,
                duplicateOfIndex: dup.index,
                duplicateReason: dup.reason,
            };
        }
        if (key) seenNumbers.set(key, index);
        if (nameKey) seenNames.set(nameKey, index);
        if (mailKey) seenEmails.set(mailKey, index);

        const hits = findSimilarCustomers(
            { name: row.name, email: row.email, zip: row.zip, city: row.city },
            existing,
        );
        const hit = key ? byNumber.get(key) : undefined;
        if (hit) {
            if (nameSimilarity(row.name, hit.name) < NUMBER_MATCH_MIN_NAME_SIMILARITY) {
                // Nummer vergeben, Name passt nicht: die Zeile darf weder still
                // verschwinden noch die fremde Nummer erben.
                return {
                    row,
                    status: 'number_conflict' as const,
                    existing: hit,
                    hits: hits.filter((h) => h.customer.id !== hit.id),
                };
            }
            return { row, status: 'exists' as const, existing: hit };
        }
        if (hits.length) return { row, status: 'similar' as const, hits };
        return { row, status: 'new' as const };
    });
};

/**
 * Felder, die im Merge-Dialog Liste-vs-Bestand waehlbar sind.
 *
 * Anrede, Titel und Kundentyp gehoeren dazu: sie kommen aus derselben Zeile
 * wie Adresse und Telefon, und ohne sie liesse sich ein Bestandskunde, der als
 * Firma angelegt wurde, nie ueber den Import zur Privatperson mit Anrede
 * korrigieren — bei einem Nummern-Treffer erschien nicht einmal der
 * "Aktualisieren"-Button.
 */
export const MERGE_FIELD_KEYS = [
    'customerNumber',
    'name',
    'firstName',
    'lastName',
    'street',
    'zip',
    'city',
    'email',
    'phone',
    'salutation',
    'title',
    'customerType',
] as const;

export type MergeFieldKey = (typeof MERGE_FIELD_KEYS)[number];

/** Je Feld: 'list' = Wert aus der Liste uebernehmen, 'existing' = Bestand. */
export type MergeChoices = Partial<Record<MergeFieldKey, 'list' | 'existing'>>;

const trimmed = (v: string | undefined): string => (v ?? '').trim();

/** Felder, in denen die Liste einen abweichenden, nicht-leeren Wert hat. */
export const mergeFieldDiffs = (
    row: ImportedCustomerRow,
    existing: Customer,
): MergeFieldKey[] =>
    MERGE_FIELD_KEYS.filter((key) => {
        const lv = trimmed(row[key]);
        return lv !== '' && lv !== trimmed(existing[key] as string | undefined);
    });

/**
 * Update aus den Feld-Entscheidungen: nur Felder mit Wahl 'list', deren Wert
 * sich wirklich unterscheidet. Aendert sich Strasse/PLZ/Ort, wird auch die
 * zusammengesetzte Anzeige-Adresse neu aufgebaut.
 */
export const buildMergeUpdate = (
    row: ImportedCustomerRow,
    existing: Customer,
    choices: MergeChoices,
): Partial<Customer> => {
    const updates: Partial<Customer> = {};
    for (const key of MERGE_FIELD_KEYS) {
        if (choices[key] !== 'list') continue;
        const lv = trimmed(row[key]);
        if (lv === '' || lv === trimmed(existing[key] as string | undefined)) continue;
        (updates as Record<string, string>)[key] = lv;
    }
    if (updates.street !== undefined || updates.zip !== undefined || updates.city !== undefined) {
        const street = updates.street ?? existing.street;
        const zip = updates.zip ?? existing.zip;
        const city = updates.city ?? existing.city;
        const place = [zip, city].filter(Boolean).join(' ');
        updates.address = [street, place].filter(Boolean).join(', ') || undefined;
    }
    // Vor-/Nachname und Anzeigename duerfen nicht auseinanderlaufen: `name` ist
    // ueberall der Vollname (PDF, Angebot, Rechnung). Wird ein Namensteil
    // uebernommen, ohne dass der Name selbst gewaehlt wurde, wird er neu
    // komponiert — nur bei Privatpersonen.
    if (updates.firstName !== undefined || updates.lastName !== undefined) {
        const type = updates.customerType ?? existing.customerType;
        if (type === 'person' && updates.name === undefined) {
            const composed = composePersonName(
                updates.firstName ?? existing.firstName,
                updates.lastName ?? existing.lastName,
            );
            if (composed && composed !== trimmed(existing.name)) updates.name = composed;
        }
    }
    return updates;
};

/** Haelt ein ANDERER Kunde diese Nummer? (Nummern sind global eindeutig.) */
export const numberTakenByOther = (
    number: string | undefined,
    customers: Customer[],
    selfId: string,
): boolean => {
    const key = normNumber(number);
    if (!key) return false;
    return customers.some(
        (c) => c.id !== selfId && normNumber(c.customerNumber) === key,
    );
};
